// A small client for the Worker's `/api/realtime/*` proxy routes. It never
// talks to Cloudflare directly and never sees the Realtime app secret — the
// Worker holds that. This file only depends on a minimal fetch-shaped
// interface, not on DOM or Workers-runtime globals, so it typechecks and
// runs identically in the browser client and in a future recorder or
// mobile client.

import type {
  LocalTrackRequest,
  NewSessionResponse,
  PullTracksRequest,
  PushTracksRequest,
  RTCSessionDescriptionLike,
  RealtimeApiError,
  RemoteTrackRequest,
  RenegotiateRequest,
  RenegotiateResponse,
  TracksResponse,
  TurnCredentialsResponse,
} from "./realtimeProtocol.js";

export interface HttpResponseLike {
  readonly ok: boolean;
  readonly status: number;
  json(): Promise<unknown>;
}

export type FetchLike = (
  url: string,
  init?: {
    readonly method?: string;
    readonly headers?: Readonly<Record<string, string>>;
    readonly body?: string;
  },
) => Promise<HttpResponseLike>;

export class RealtimeApiRequestError extends Error {
  readonly status: number;
  readonly body: RealtimeApiError;

  constructor(status: number, body: RealtimeApiError) {
    super(body.message);
    this.name = "RealtimeApiRequestError";
    this.status = status;
    this.body = body;
  }
}

export interface WorkerRealtimeClientOptions {
  /** e.g. "" for same-origin, or a full origin for testing against a
   * different host. */
  readonly baseUrl: string;
  readonly fetchImpl: FetchLike;
}

/** Thin client for the Worker's `/api/realtime/*` routes. Every method
 * throws a `RealtimeApiRequestError` on a non-2xx response, carrying the
 * Worker's own typed error body rather than a bare status code. */
export class WorkerRealtimeClient {
  private readonly opts: WorkerRealtimeClientOptions;

  constructor(opts: WorkerRealtimeClientOptions) {
    this.opts = opts;
  }

  private async request<T>(path: string, method: string, body?: unknown): Promise<T> {
    const res = await this.opts.fetchImpl(`${this.opts.baseUrl}${path}`, {
      method,
      headers: { "content-type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const json = await res.json();
    if (!res.ok) {
      throw new RealtimeApiRequestError(res.status, json as RealtimeApiError);
    }
    return json as T;
  }

  newSession(): Promise<NewSessionResponse> {
    return this.request<NewSessionResponse>("/api/realtime/sessions", "POST");
  }

  pushTracks(
    sessionId: string,
    sessionDescription: RTCSessionDescriptionLike,
    tracks: readonly LocalTrackRequest[],
  ): Promise<TracksResponse> {
    const body: PushTracksRequest = { sessionId, sessionDescription, tracks };
    return this.request<TracksResponse>(
      `/api/realtime/sessions/${encodeURIComponent(sessionId)}/tracks/new`,
      "POST",
      body,
    );
  }

  pullTracks(sessionId: string, tracks: readonly RemoteTrackRequest[]): Promise<TracksResponse> {
    const body: PullTracksRequest = { sessionId, tracks };
    return this.request<TracksResponse>(
      `/api/realtime/sessions/${encodeURIComponent(sessionId)}/tracks/new`,
      "POST",
      body,
    );
  }

  renegotiate(sessionId: string, sessionDescription: RTCSessionDescriptionLike): Promise<RenegotiateResponse> {
    const body: RenegotiateRequest = { sessionId, sessionDescription };
    return this.request<RenegotiateResponse>(
      `/api/realtime/sessions/${encodeURIComponent(sessionId)}/renegotiate`,
      "PUT",
      body,
    );
  }

  closeTracks(sessionId: string, mids: readonly string[], force: boolean): Promise<void> {
    return this.request<void>(
      `/api/realtime/sessions/${encodeURIComponent(sessionId)}/tracks/close`,
      "PUT",
      { sessionId, tracks: mids.map((mid) => ({ mid })), force },
    );
  }

  /** Changes which simulcast layer already-pulled videos receive, with no
   * renegotiation: each entry names the pulled track by its local mid and
   * the publisher's session and track name. */
  updateTracks(
    sessionId: string,
    tracks: readonly (RemoteTrackRequest & { readonly mid: string })[],
  ): Promise<TracksResponse> {
    return this.request<TracksResponse>(
      `/api/realtime/sessions/${encodeURIComponent(sessionId)}/tracks/update`,
      "PUT",
      { tracks },
    );
  }

  turnCredentials(): Promise<TurnCredentialsResponse> {
    return this.request<TurnCredentialsResponse>("/api/realtime/turn-credentials", "POST");
  }
}
